import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Calendar, Search, Plus, Eye, MapPin, Phone, 
  Mail, UserPlus, Clock, CheckCircle, Filter, ArrowLeft, Activity,
  Stethoscope, AlertCircle, Shield, MessageCircle, TrendingDown, Pill, FlaskConical, FileText, AlertTriangle, Upload,
  User,
  Video,
  Home
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { CareTeamManagement } from './coordinator/CareTeamManagement';
import { PatientAssignmentScreen } from './coordinator/PatientAssignmentScreen';
import { CoordinatorSymptomsTracker } from './coordinator/CoordinatorSymptomsTracker';
import { DischargeSummaryOnboarding } from './shared/DischargeSummaryOnboarding';
import { RegisterPatientModal } from './shared/RegisterPatientModal';
import { ScheduleAppointmentModal } from './coordinator/ScheduleAppointmentModal';
import { useCoordinatorDashboard } from '../lib/hooks/useDashboards';
import { useAuth } from '../lib/hooks/useAuth';
import { useCreateHospital, useHospitals } from '../lib/hooks/useHospitals';
import { useCreateAppointment } from '../lib/hooks/useAppointments';
import { useAssignPatient } from '../lib/hooks/usePatients';
import { AppointmentType } from '../lib/api/appointments';
import { LoadingState } from './common/LoadingState';
import { AlertCard } from './common/AlertCard';
import { EmptyState } from './common/EmptyState';
import { UploadDischargeSummaryModal } from './physician_assistant/UploadDischargeSummaryModal';
import { PatientDetailsView } from './PatientDetailsView';
import { VideoCallScreen } from './shared/VideoCallScreen';
import { HomeNurseTasksViewCompact } from './doctor/HomeNurseTasksViewCompact';

interface CoordinatorDashboardProps {
  onNavigate?: (view: 'dashboard' | 'exercise' | 'vitals' | 'reports' | 'messaging' | 'appointments' | 'library' | 'settings' | 'notifications') => void;
}

// Type definitions
interface CareTeam {
  id: string;
  name: string;
  hospitalId: string;
  hospitalName: string;
  doctors: string[];
  patientCount: number;
}

interface PatientAssignment {
  id: string;
  patientId: string;
  careTeamId: string;
  nurseId: string;
  physiotherapistId: string;
  assignedDate: string;
}

interface Hospital {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  totalPatients: number;
  totalDoctors: number;
  registeredDate: string;
  status: 'Active' | 'Inactive';
}

interface AppointmentData {
  patientId: string;
  patientName: string;
  doctorId: string;
  doctorName: string;
  date: string;
  time: string;
  reason: string;
  notes: string;
}

export function CoordinatorDashboard({ onNavigate }: CoordinatorDashboardProps) {
  // ====== ALL HOOKS MUST BE DECLARED FIRST - BEFORE ANY RETURNS ======
  
  // Get authenticated coordinator
  const { user } = useAuth();
  
  // Fetch dashboard data from API
  const { data: dashboardData, isLoading, error, refetch } = useCoordinatorDashboard(true);
  
  // Fetch hospitals independently using the useHospitals hook
  const { data: hospitalsFromAPI, isLoading: hospitalsLoading, refetch: refetchHospitals } = useHospitals({ is_active: true });
  
  // Hospital creation mutation
  const createHospitalMutation = useCreateHospital();
  
  // Appointment creation mutation
  const createAppointmentMutation = useCreateAppointment();
  
  // Patient assignment mutation
  const assignPatientMutation = useAssignPatient();
  
  // ALL useState hooks
  const [activeSection, setActiveSection] = useState<'overview' | 'hospitals' | 'patients' | 'appointments' | 'symptoms' | 'care-teams' | 'assignments' | 'low-adherence' | 'onboarding'>('overview');
  const [viewingPatient, setViewingPatient] = useState<any>(null);
  const [viewingSymptomHistory, setViewingSymptomHistory] = useState<any>(null);
  const [showRegisterHospital, setShowRegisterHospital] = useState(false);
  const [showScheduleAppointment, setShowScheduleAppointment] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [careTeams, setCareTeams] = useState<CareTeam[]>([]);
  const [patientAssignments, setPatientAssignments] = useState<PatientAssignment[]>([]);
  const [selectedOnboardingPatient, setSelectedOnboardingPatient] = useState<any | null>(null);
  const [completedOnboarding, setCompletedOnboarding] = useState<string[]>([]);
  const [escalatedSymptoms, setEscalatedSymptoms] = useState<Set<string>>(new Set());
  const [newHospitalDialogOpen, setNewHospitalDialogOpen] = useState(false);
  const [hospitalsData, setHospitalsData] = useState<Hospital[]>([]);
  const [selectedPatientForUpload, setSelectedPatientForUpload] = useState<any | null>(null);
  const [isUploadDischargeSummaryModalOpen, setIsUploadDischargeSummaryModalOpen] = useState(false);
  const [activeVideoCall, setActiveVideoCall] = useState<any>(null);
  const [activeView, setActiveView] = useState<'list' | 'details' | 'adherence-detail' | 'nurse-tasks'>('list');
  const [selectedPatientNurse, setSelectedPatientNurse] = useState<any | null>(null);

  const [newHospital, setNewHospital] = useState<Partial<Hospital>>({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    status: 'Active'
  });
  const [hospitalFormErrors, setHospitalFormErrors] = useState<Record<string, string>>({});
  const [hospitalFormError, setHospitalFormError] = useState<string>('');
  const [appointmentData, setAppointmentData] = useState<AppointmentData>({
    patientId: '',
    patientName: '',
    doctorId: '',
    doctorName: '',
    date: '',
    time: '',
    reason: '',
    notes: ''
  });
  const [isRegisterPatientModalOpen, setIsRegisterPatientModalOpen] = useState(false);
  
  // Update state when dashboardData changes
  useEffect(() => {
    if (dashboardData) {
      setCareTeams(dashboardData.care_teams || []);
      setPatientAssignments(dashboardData.patient_assignments || []);
      setHospitalsData(dashboardData.hospitals || []);
    }
  }, [dashboardData]);
  
  // ====== NOW WE CAN USE CONDITIONAL RETURNS ======
  
  // Show loading state
  if (isLoading) {
    return <LoadingState message="Loading coordinator dashboard..." />;
  }
  
  // Show error state
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <AlertCard
          type="error"
          title="Failed to load dashboard"
          description={error?.message || "An error occurred while loading the dashboard"}
          action={
            <Button onClick={() => refetch()} variant="outline">
              Try Again
            </Button>
          }
        />
      </div>
    );
  }
  
  // Show empty state if no data
  if (!dashboardData) {
    return (
      <div className="container mx-auto p-6">
        <EmptyState 
          title="No dashboard data available" 
          description="Please contact support if this issue persists"
        />
      </div>
    );
  }

  // Use API data with fallbacks
  const stats = dashboardData?.stats || {
    total_cohorts: 0,
    total_patients: 0,
    pending_assignments: 0,
    avg_adherence: 0,
  };

  const cohorts = dashboardData?.cohorts || [];
  const careTeamStats = dashboardData?.care_team || {};
  const hospitals = hospitalsFromAPI || dashboardData?.hospitals || []; // Use hospitalsFromAPI first, then fallback to dashboard data
  const allPatients = dashboardData?.all_patients || [];
  const availableDoctors = dashboardData?.available_doctors || [];
  const scheduledAppointments = dashboardData?.scheduled_appointments || [];
  const allSymptoms = dashboardData?.all_symptoms || [];
  const lowAdherencePatients = dashboardData?.low_adherence_patients || [];

  // Filter patients by hospital and search
  const filteredPatients = allPatients.filter(patient => {
    const matchesHospital = selectedHospital === 'all' || patient.hospital_id === parseInt(selectedHospital);
    const matchesSearch = patient.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || patient.status === statusFilter;
    return matchesHospital && matchesSearch && matchesStatus;
  });

  // Helper to get adherence detail data from API
  const getPatientAdherenceDetail = (patient: any) => {
    // Use adherence_breakdown from API if available
    if (patient.adherence_breakdown) {
      return {
        medication: {
          overall: patient.adherence_breakdown.medication?.adherence_percentage || patient.adherence || 0,
          scheduledDoses: patient.adherence_breakdown.medication?.doses_scheduled || 0,
          takenDoses: patient.adherence_breakdown.medication?.doses_taken || 0,
          missedDoses: (patient.adherence_breakdown.medication?.doses_scheduled || 0) - (patient.adherence_breakdown.medication?.doses_taken || 0),
        },
        exercise: {
          overall: patient.adherence_breakdown.exercise?.adherence_percentage || patient.adherence || 0,
          assignedExercises: 21,
          completedExercises: patient.adherence_breakdown.exercise?.sessions_completed || 0,
        },
        homeTest: {
          overall: patient.adherence_breakdown.home_tests?.adherence_percentage || patient.adherence || 0,
          scheduledTests: 6,
          completedTests: patient.adherence_breakdown.home_tests?.tests_completed || 0,
        }
      };
    }
    
    // Fallback to basic structure if no breakdown available
    return {
      medication: { overall: patient.adherence || 0, scheduledDoses: 0, takenDoses: 0, missedDoses: 0 },
      exercise: { overall: patient.adherence || 0, assignedExercises: 0, completedExercises: 0 },
      homeTest: { overall: patient.adherence || 0, scheduledTests: 0, completedTests: 0 }
    };
  };

  const handleRegisterHospital = async () => {
    // Clear previous errors
    setHospitalFormErrors({});
    setHospitalFormError('');
    
    // Basic frontend validation
    const errors: Record<string, string> = {};
    if (!newHospital.name?.trim()) errors.name = 'Hospital name is required';
    if (!newHospital.city?.trim()) errors.city = 'City is required';
    if (!newHospital.phone?.trim()) errors.phone = 'Phone is required';
    
    if (Object.keys(errors).length > 0) {
      setHospitalFormErrors(errors);
      toast.error('Please fill in all required fields');
      return;
    }

    // Call backend API
    try {
      await createHospitalMutation.mutateAsync({
        name: newHospital.name!,
        address: newHospital.address || undefined,
        city: newHospital.city!,
        state: newHospital.state || undefined,
        pincode: newHospital.zipCode || undefined, // Backend uses 'pincode' not 'zipCode'
        phone: newHospital.phone!,
        email: newHospital.email || undefined,
        is_active: true
      });

      // Success! Close dialog and reset form
      setShowRegisterHospital(false);
      setNewHospital({
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        email: '',
        status: 'Active'
      });
      setHospitalFormErrors({});
      setHospitalFormError('');
      
      // Refetch dashboard data to get updated hospital list
      refetch();
    } catch (error: any) {
      // Extract validation errors from backend response
      const backendErrors = error?.response?.data?.detail;
      
      if (typeof backendErrors === 'string') {
        // Single error message
        setHospitalFormError(backendErrors);
      } else if (Array.isArray(backendErrors)) {
        // Array of validation errors from FastAPI
        const fieldErrors: Record<string, string> = {};
        backendErrors.forEach((err: any) => {
          const field = err.loc?.[err.loc.length - 1]; // Get the field name
          const message = err.msg || 'Invalid value';
          if (field) {
            fieldErrors[field] = message;
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          setHospitalFormErrors(fieldErrors);
        } else {
          setHospitalFormError('Validation failed. Please check your inputs.');
        }
      } else if (typeof backendErrors === 'object') {
        // Object with field names as keys
        setHospitalFormErrors(backendErrors);
      } else {
        setHospitalFormError('Failed to register hospital. Please try again.');
      }
      
      console.error('Failed to register hospital:', error);
    }
  };

  const handleScheduleAppointment = async () => {
    if (!appointmentData.patientId || !appointmentData.doctorId || !appointmentData.date || !appointmentData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Combine date and time into scheduled_date format (ISO 8601)
      const scheduledDate = `${appointmentData.date}T${appointmentData.time}:00`;
      
      await createAppointmentMutation.mutateAsync({
        patient_id: parseInt(appointmentData.patientId),
        doctor_id: parseInt(appointmentData.doctorId),
        appointment_type: AppointmentType.IN_PERSON, // Default to in-person, can be made selectable
        scheduled_date: scheduledDate,
        duration_minutes: 30, // Default 30 minutes, can be made configurable
        reason: appointmentData.reason || 'Consultation',
        notes: appointmentData.notes || undefined,
      });

      // Success! Close dialog and reset form
      setShowScheduleAppointment(false);
      setAppointmentData({
        patientId: '',
        patientName: '',
        doctorId: '',
        doctorName: '',
        date: '',
        time: '',
        reason: '',
        notes: ''
      });
      
      // Refetch dashboard to update appointments list
      refetch();
    } catch (error: any) {
      // Error toast already shown by the mutation hook
      console.error('Failed to schedule appointment:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'On Track':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      case 'Missed':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      case 'Escalated':
        return 'bg-destructive/10 text-destructive border-destructive';
      default:
        return '';
    }
  };

  const handleViewHomeNurseTasks = (patient: any) => {
    if (!patient?.id) {
      toast.error('Unable to load home nurse tasks. Patient ID is missing.');
      return;
    }
    setSelectedPatientNurse(patient);
    setActiveView('nurse-tasks');
    toast.info(`Loading ${patient.name || 'patient'}'s home nurse tasks...`);
  };

  const handleBackToList = () => {
    setViewingPatient(null);
    setActiveView("list");
  };
  
  // Care Team Handlers
  const handleCreateCareTeam = (newTeam: any) => {
    const careTeam: CareTeam = {
      id: `CT${careTeams.length + 1}`,
      name: newTeam.name,
      hospitalId: newTeam.hospitalId,
      hospitalName: newTeam.hospitalName,
      doctors: newTeam.doctorIds,
      patientCount: 0,
    };
    setCareTeams([...careTeams, careTeam]);
  };

  const handleViewTeamPatients = (teamId: string) => {
    setActiveSection('patients');
    toast.info('Viewing patients for selected care team');
  };

  const handlePatientAssignment = (assignment: PatientAssignment) => {
    const existingIndex = patientAssignments.findIndex(a => a.patientId === assignment.patientId);
    if (existingIndex >= 0) {
      const updated = [...patientAssignments];
      updated[existingIndex] = {
        ...assignment,
        assignedDate: new Date().toISOString().split('T')[0],
      };
      setPatientAssignments(updated);
    } else {
      setPatientAssignments([
        ...patientAssignments,
        {
          ...assignment,
          assignedDate: new Date().toISOString().split('T')[0],
        },
      ]);
    }
  };

  // Helper function to check if appointment can be joined
  const canJoinAppointment = (datetime: string) => {
    const now = new Date();
    const [date, time] = datetime.split("T");
    // Parse the appointment date and time
    const [year, month, day] = date.split('-').map(Number);
    const timeStr = time.toLowerCase();
    const [timeOnly, period] = timeStr.split(' ');
    const [hours, minutes] = timeOnly.split(':').map(Number);
    
    // Convert to 24-hour format
    let appointmentHours = hours;
    if (period === 'pm' && hours !== 12) {
      appointmentHours = hours + 12;
    } else if (period === 'am' && hours === 12) {
      appointmentHours = 0;
    }
    
    const appointmentDate = new Date(year, month - 1, day, appointmentHours, minutes);
    
    // Allow joining if appointment time is now or in the past
    return appointmentDate <= now;
  };

  // Handle joining video call
  const handleJoinCall = (appointment: any) => {
    setActiveVideoCall(appointment);
  };

  // Handle ending video call
  const handleEndCall = () => {
    setActiveVideoCall(null);
    toast.success('Call ended successfully');
  };

  // Show video call screen if active
  if (activeVideoCall) {    
    return (
      <VideoCallScreen
        sessionId='TestSession#1'
        patientName={activeVideoCall.patient_name}
        doctorName={activeVideoCall.doctor_name}
        onEndCall={handleEndCall}
      />
    );
  }

  // Patient details view
  if (viewingPatient) {
    return (
      <PatientDetailsView
        patient={viewingPatient}
        onBack={() => setViewingPatient(null)}
        userRole="physician_assistant"
      />
    );
  }

  // Home Nurse Tasks View
  if (activeView === 'nurse-tasks' && selectedPatientNurse) {
    return (
      <div className="min-h-screen bg-[#D4F0F8]">
        <div className="bg-white border-b border-border sticky top-0 z-40 shadow-sm">
          <div className="px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={handleBackToList} size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Back to Patient List</span>
                <span className="sm:hidden">Back</span>
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-primary">{selectedPatientNurse.name || 'Patient'}</h1>
                <p className="text-sm text-muted-foreground">Home Nurse Tasks</p>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 md:px-6 lg:px-8 py-6">
          <HomeNurseTasksViewCompact
            patientId={selectedPatientNurse?.id?.toString() || '0'}
            patientName={selectedPatientNurse?.name || 'Patient'}
            patientAge={selectedPatientNurse?.age}
            patientGender={selectedPatientNurse?.gender}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-4 sm:mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="truncate text-lg sm:text-2xl">Welcome, {user?.first_name || 'Coordinator'}</h1>
            <p className="text-muted-foreground text-xs sm:text-sm truncate">Care Coordinator - Healthcare Coordination</p>
          </div>
          <Button
            onClick={() => setIsRegisterPatientModalOpen(true)}
            className="bg-primary hover:bg-primary/90 gap-2 flex-shrink-0"
            size="sm"
          >
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Register New Patient</span>
            <span className="sm:hidden">Register</span>
          </Button>
        </div>

        {/* Top Menu Navigation */}
        <div className="mb-4 sm:mb-6 flex flex-wrap gap-2">
          <Button
            variant={activeSection === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveSection('overview')}
            className={activeSection === 'overview' ? 'bg-primary' : ''}
            size="sm"
          >
            <Activity className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Overview</span>
          </Button>
          <Button
            variant={activeSection === 'hospitals' ? 'default' : 'outline'}
            onClick={() => setActiveSection('hospitals')}
            className={activeSection === 'hospitals' ? 'bg-primary' : ''}
            size="sm"
          >
            <Building2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Hospitals</span>
          </Button>
          <Button
            variant={activeSection === 'patients' ? 'default' : 'outline'}
            onClick={() => setActiveSection('patients')}
            className={activeSection === 'patients' ? 'bg-primary' : ''}
            size="sm"
          >
            <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Patients</span>
          </Button>
          <Button
            variant={activeSection === 'appointments' ? 'default' : 'outline'}
            onClick={() => setActiveSection('appointments')}
            className={activeSection === 'appointments' ? 'bg-primary' : ''}
            size="sm"
          >
            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Appointments</span>
          </Button>
          <Button
            variant={activeSection === 'symptoms' ? 'default' : 'outline'}
            onClick={() => setActiveSection('symptoms')}
            className={activeSection === 'symptoms' ? 'bg-primary' : ''}
            size="sm"
          >
            <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Symptoms</span>
          </Button>
          <Button
            variant={activeSection === 'care-teams' ? 'default' : 'outline'}
            onClick={() => setActiveSection('care-teams')}
            className={activeSection === 'care-teams' ? 'bg-primary' : ''}
            size="sm"
          >
            <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Care Teams</span>
          </Button>
          <Button
            variant={activeSection === 'assignments' ? 'default' : 'outline'}
            onClick={() => setActiveSection('assignments')}
            className={activeSection === 'assignments' ? 'bg-primary' : ''}
            size="sm"
          >
            <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Assignments</span>
          </Button>
          <Button
            variant={activeSection === 'low-adherence' ? 'default' : 'outline'}
            onClick={() => setActiveSection('low-adherence')}
            className={activeSection === 'low-adherence' ? 'bg-primary' : ''}
            size="sm"
          >
            <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Low Adherence</span>
          </Button>
          <Button
            variant={activeSection === 'onboarding' ? 'default' : 'outline'}
            onClick={() => setActiveSection('onboarding')}
            className={`${activeSection === 'onboarding' ? 'bg-primary' : ''} relative`}
            size="sm"
          >
            <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">New Patients</span>
            {(dashboardData?.new_patients_pending_onboarding || []).filter(p => !completedOnboarding.includes(p.patient_id || p.id)).length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-accent text-white rounded-full flex items-center justify-center text-[10px] sm:text-xs">
                {(dashboardData?.new_patients_pending_onboarding || []).filter(p => !completedOnboarding.includes(p.patient_id || p.id)).length}
              </span>
            )}
          </Button>
          <Button
            onClick={() => onNavigate?.('messaging')}
            className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white"
            size="sm"
          >
            <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="text-xs sm:text-sm">Chat</span>
          </Button>
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            <div className="flex gap-3 sm:gap-4 md:gap-6" style={{ justifyContent: 'center' }}>
              {/* Total Hospitals */}
              <Card className="shadow-lg">
                <CardContent className="p-4 sm:p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs sm:text-sm truncate">Total Hospitals</p>
                      <h2 className="text-primary text-xl sm:text-2xl">{hospitals?.length || 0}</h2>
                    </div>
                    <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0 ml-2" />
                  </div>
                </CardContent>
              </Card>

              {/* Total Patients */}
              <Card className="shadow-lg">
                <CardContent className="p-4 sm:p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs sm:text-sm truncate">Total Patients</p>
                      <h2 className="text-[#14CAF4] text-xl sm:text-2xl">{stats.total_patients}</h2>
                    </div>
                    <Users className="w-6 h-6 sm:w-8 sm:h-8 text-[#14CAF4] flex-shrink-0 ml-2" />
                  </div>
                </CardContent>
              </Card>

              {/* Scheduled Today */}
              <Card className="shadow-lg">
                <CardContent className="p-4 sm:p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs sm:text-sm truncate">Scheduled Today</p>
                      <h2 className="text-accent text-xl sm:text-2xl">{scheduledAppointments?.filter((apt: any) => {
                        const today = new Date().toISOString().split('T')[0];
                        return apt.scheduled_date?.startsWith(today);
                      }).length || 0}</h2>
                    </div>
                    <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-accent flex-shrink-0 ml-2" />
                  </div>
                </CardContent>
              </Card>

              {/* Total Appointments */}
              <Card className="shadow-lg">
                <CardContent className="p-4 sm:p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs sm:text-sm truncate">Total Appointments</p>
                      <h2 className="text-primary text-xl sm:text-2xl">{scheduledAppointments?.length || 0}</h2>
                    </div>
                    <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0 ml-2" />
                  </div>
                </CardContent>
              </Card>

              {/* New Patients */}
              <Card className="shadow-lg">
                <CardContent className="p-4 sm:p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs sm:text-sm truncate">New Patients</p>
                      <h2 className="text-accent text-xl sm:text-2xl">{(dashboardData?.new_patients_pending_onboarding || []).length}</h2>
                    </div>
                    <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-accent flex-shrink-0 ml-2" />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Action Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Hospital Management Card */}
              <Card className="bg-[#2762AC]/5 border-[#2762AC]/20">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-[#2762AC]/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-[#2762AC]" />
                      </div>
                      <h3 className="text-[#2762AC] text-lg font-semibold">Hospital Management</h3>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Register new hospitals and manage existing healthcare facilities in the network
                    </p>
                    <Button 
                      onClick={() => setShowRegisterHospital(true)}
                      className="w-full bg-[#2762AC] hover:bg-[#2762AC]/90 text-white gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Register New Hospital
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Appointment Scheduling Card */}
              <Card className="bg-[#F07D13]/5 border-[#F07D13]/20">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-[#F07D13]/10 flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-[#F07D13]" />
                      </div>
                      <h3 className="text-[#F07D13] text-lg font-semibold">Appointment Scheduling</h3>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Schedule patient appointments with doctors across all registered hospitals
                    </p>
                    <Button 
                      onClick={() => setShowScheduleAppointment(true)}
                      className="w-full bg-[#F07D13] hover:bg-[#F07D13]/90 text-white gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Schedule New Appointment
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Latest Symptoms and Appointments Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Latest Symptoms Reported */}
              <Card className="border-[#F07D13]/20">
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-primary text-base sm:text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#F07D13]" />
                    Latest Symptoms Reported
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 sm:p-6">
                  {allSymptoms.length === 0 ? (
                    <div className="text-center py-6">
                      <CheckCircle className="w-10 h-10 text-[#14CAF4] mx-auto mb-2" />
                      <p className="text-muted-foreground text-sm">No symptoms reported</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {allSymptoms.slice(0, 5).map((symptom: any) => (
                        <div key={symptom.id} className="flex items-start justify-between gap-3 p-3 bg-[#F07D13]/5 rounded-lg border border-[#F07D13]/10 hover:shadow-md transition-shadow cursor-pointer">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-primary text-sm truncate">{symptom.patient_name}</h4>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{symptom.symptoms}</p>
                            <p className="text-xs text-muted-foreground mt-1">{symptom.date}</p>
                          </div>
                          <Badge className={`flex-shrink-0 text-xs ${
                            symptom.severity === 'Severe' || symptom.severity === 'severe' ? 'bg-destructive text-white' :
                            symptom.severity === 'Moderate' || symptom.severity === 'moderate' ? 'bg-[#F07D13] text-white' :
                            'bg-[#14CAF4] text-white'
                          }`}>
                            {symptom.severity}
                          </Badge>
                        </div>
                      ))}
                      {allSymptoms.length > 5 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full mt-2"
                          onClick={() => setActiveSection('symptoms')}
                        >
                          View All Symptoms ({allSymptoms.length})
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upcoming Appointments */}
              <Card className="border-[#14CAF4]/20">
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-primary text-base sm:text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#14CAF4]" />
                    Upcoming Appointments
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 sm:p-6">
                  {scheduledAppointments.length === 0 ? (
                    <div className="text-center py-6">
                      <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
                      <p className="text-muted-foreground text-sm">No upcoming appointments</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {scheduledAppointments.slice(0, 5).map((apt: any) => (
                        <div key={apt.id} className="p-3 bg-[#14CAF4]/5 rounded-lg border border-[#14CAF4]/10 hover:shadow-md transition-shadow cursor-pointer">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-primary text-sm truncate">{apt.patient_name}</h4>
                              <p className="text-xs text-muted-foreground truncate">
                                with Dr. {apt.doctor_name}
                              </p>
                            </div>
                            <Badge className={`flex-shrink-0 text-xs ${
                              apt.status === 'CONFIRMED' ? 'bg-[#14CAF4] text-white' :
                              apt.status === 'PENDING' ? 'bg-[#F07D13] text-white' :
                              apt.status === 'COMPLETED' ? 'bg-[#2762AC] text-white' :
                              'bg-[#494949] text-white'
                            }`}>
                              {apt.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            <span>{apt.appointment_date} at {apt.appointment_time}</span>
                          </div>
                          {apt.reason && (
                            <p className="text-xs text-muted-foreground mt-1 truncate">Reason: {apt.reason}</p>
                          )}
                        </div>
                      ))}
                      {scheduledAppointments.length > 5 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full mt-2"
                          onClick={() => setActiveSection('appointments')}
                        >
                          View All Appointments ({scheduledAppointments.length})
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
        
        {/* Care Teams Section */}
        {activeSection === 'care-teams' && (
          <CareTeamManagement 
            hospitals={hospitals}
            doctors={dashboardData?.available_doctors || []}
            therapists={dashboardData?.available_therapists || []}
            nurses={dashboardData?.available_nurses || []}
            physicianAssistants={dashboardData?.available_physician_assistants || []}
          />
        )}
        
        {/* Assignments Section */}
        {activeSection === 'assignments' && (
          <PatientAssignmentScreen 
            patients={allPatients}
            doctors={dashboardData?.available_doctors || []}
            physiotherapists={dashboardData?.available_therapists || []}
            nurses={dashboardData?.available_nurses || []}
            assignments={dashboardData?.pending_assignments || []}
            onAssign={(assignment) => {
              assignPatientMutation.mutate({
                patient_id: assignment.patient_id,
                care_team_id: assignment.care_team_id,
              });
            }}
          />
        )}
        
        {/* Low Adherence Section */}
        {activeSection === 'low-adherence' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-primary">Low Adherence Patients</CardTitle>
              </CardHeader>
              <CardContent>
                {lowAdherencePatients.length === 0 ? (
                  <EmptyState 
                    title="No low adherence patients"
                    description="All patients are maintaining good adherence"
                  />
                ) : (
                  <div className="space-y-4">
                    {lowAdherencePatients.map((patient: any) => (
                      <Card key={patient.id} className="border-[#F07D13]/20">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-primary">{patient.name}</h3>
                              <p className="text-sm text-muted-foreground">UMR: {patient.umr_no}</p>
                            </div>
                            <Badge className="bg-[#F07D13] text-white">
                              {patient.adherence_score}% Adherence
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Patients Section */}
        {activeSection === 'patients' && (
          <div className="space-y-4">

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 bg-white p-4 rounded-lg border border-gray-200">

              {/* Hospital Filter */}
              <Select value={selectedHospital} onValueChange={setSelectedHospital}>
                <SelectTrigger className="w-full sm:w-[200px] bg-gray-50">
                  <SelectValue placeholder="All Hospitals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hospitals</SelectItem>
                  {hospitals.map(h => (
                    <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Search Input */}
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search patients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-white"
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[150px] bg-gray-50">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ACTIVE">On Track</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Patient List */}
            <div className="space-y-3">

              {allPatients.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-muted-foreground">No patients found</p>
                    <p className="text-sm text-muted-foreground mt-1">No patients assigned to you yet</p>
                  </CardContent>
                </Card>
              ) : filteredPatients.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-muted-foreground">No matching patients</p>
                    <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters</p>
                  </CardContent>
                </Card>
              ) : (

                filteredPatients.map((patient: any) => {
                  const getInitials = (name: string) =>
                    name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

                  const getStatusBadgeStyle = (status: string) => {
                    const s = status?.toLowerCase() || "";
                    if (s.includes("track") || s === "active") return "bg-[#14CAF4] text-white text-xs px-2 py-1";
                    if (s.includes("recent")) return "bg-orange-100 text-orange-700 text-xs px-2 py-1 border border-orange-300";
                    if (s.includes("developed") || s.includes("lap")) return "bg-red-100 text-red-700 text-xs px-2 py-1 border border-red-300";
                    return "bg-gray-100 text-gray-700 text-xs px-2 py-1";
                  };

                  return (
                    <Card key={patient.id} className="hover:shadow-md transition-shadow border border-gray-200">
                      <CardContent className="p-4">

                        {/* 🔥 MOBILE FRIENDLY ROW → STACK DOWN ON SMALL SCREENS */}
                        <div className="flex flex-col sm:flex-row justify-between gap-4 w-full">

                          {/* LEFT SIDE : PROFILE + DETAILS */}
                          <div className="flex flex-col sm:flex-row items-start gap-3 flex-1 min-w-0">

                            {/* Avatar */}
                            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white">
                              <span className="text-sm">{getInitials(patient.name)}</span>
                            </div>

                            {/* Patient Basic Info */}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-primary truncate">
                                <User className="w-4 h-4 inline-block mb-1" /> {patient.name}
                                &nbsp;
                                <Badge className={getStatusBadgeStyle(patient.status || "Active")}>
                                  {patient.status || "On Track"}
                                </Badge>
                              </h3>
                              <p className="text-sm text-muted-foreground">{patient.gender}, {patient.age} years</p>
                              <p className="text-sm text-muted-foreground truncate">{patient.hospital_name || "N/A"}</p>
                              <p className="text-sm text-muted-foreground truncate">UMR No : {patient.umr_no || "N/A"}</p>
                            </div>

                            {/* Phone + Address */}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-primary">
                                <Phone className="w-4 h-4 inline-block mb-1" /> {patient.phone || "N/A"}
                              </h3>
                              <p className="text-sm text-muted-foreground truncate">
                                <MapPin className="w-4 h-4 inline-block mb-1 mr-1" />{patient.address}
                              </p>
                            </div>
                          </div>

                          {/* 🔥 BUTTONS STACK ON MOBILE — ROW ON DESKTOP */}
                          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">

                            <Button size="sm" className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white"
                              onClick={() => setViewingPatient(patient)}>
                              View
                            </Button>

                            <Button variant="ghost" size="sm"
                              className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white"
                              onClick={() => handleViewHomeNurseTasks(patient)}>
                              <Home className="w-4 h-4 mr-1" /> Home Nurse Tasks
                            </Button>

                            <Button size="sm" className="bg-[#F07D13] hover:bg-[#F07D13]/90 text-white"
                              onClick={() => {
                                setAppointmentData({ ...appointmentData, patientId: patient.id.toString(), patientName: patient.name });
                                setShowScheduleAppointment(true);
                              }}>
                              Appointment
                            </Button>

                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Hospitals Section */}
        {activeSection === 'hospitals' && (
          <div className="space-y-6">
            {/* Header with Add Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-primary text-xl sm:text-2xl">Registered Hospitals</h2>
              </div>
              <Dialog open={showRegisterHospital} onOpenChange={setShowRegisterHospital}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90 gap-2">
                    <Plus className="w-4 h-4" />
                    Register Hospital
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-primary">Register New Hospital</DialogTitle>
                    <DialogDescription>
                      Add a new hospital to the network
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="name">Hospital Name *</Label>
                        <Input
                          id="name"
                          value={newHospital.name}
                          onChange={(e) => setNewHospital({ ...newHospital, name: e.target.value })}
                          placeholder="Enter hospital name"
                          className={hospitalFormErrors.name ? 'border-red-500' : ''}
                        />
                        {hospitalFormErrors.name && (
                          <p className="text-red-500 text-xs mt-1">{hospitalFormErrors.name}</p>
                        )}
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="address">Address</Label>
                        <Input
                          id="address"
                          value={newHospital.address}
                          onChange={(e) => setNewHospital({ ...newHospital, address: e.target.value })}
                          placeholder="Street address"
                        />
                      </div>
                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          value={newHospital.city}
                          onChange={(e) => setNewHospital({ ...newHospital, city: e.target.value })}
                          placeholder="City"
                          className={hospitalFormErrors.city ? 'border-red-500' : ''}
                        />
                        {hospitalFormErrors.city && (
                          <p className="text-red-500 text-xs mt-1">{hospitalFormErrors.city}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          value={newHospital.state}
                          onChange={(e) => setNewHospital({ ...newHospital, state: e.target.value })}
                          placeholder="State"
                        />
                      </div>
                      <div>
                        <Label htmlFor="zipCode">ZIP Code</Label>
                        <Input
                          id="zipCode"
                          value={newHospital.zipCode}
                          onChange={(e) => setNewHospital({ ...newHospital, zipCode: e.target.value })}
                          placeholder="ZIP code"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          value={newHospital.phone}
                          onChange={(e) => setNewHospital({ ...newHospital, phone: e.target.value })}
                          placeholder="Phone number"
                          className={hospitalFormErrors.phone ? 'border-red-500' : ''}
                        />
                        {hospitalFormErrors.phone && (
                          <p className="text-red-500 text-xs mt-1">{hospitalFormErrors.phone}</p>
                        )}
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={newHospital.email}
                          onChange={(e) => setNewHospital({ ...newHospital, email: e.target.value })}
                          placeholder="Email address"
                        />
                      </div>
                    </div>
                    {hospitalFormError && (
                      <p className="text-red-500 text-xs mt-1">{hospitalFormError}</p>
                    )}
                    <div className="flex justify-end gap-3 pt-4">
                      <Button variant="outline" onClick={() => setShowRegisterHospital(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleRegisterHospital} className="bg-primary hover:bg-primary/90">
                        Register Hospital
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Hospital Cards Grid */}
            {hospitals.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-muted-foreground">No hospitals registered</p>
                  <p className="text-sm text-muted-foreground mt-1">Register your first hospital to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {hospitals.map((hospital: any) => (
                  <Card key={hospital.id} className="hover:shadow-lg transition-shadow border border-gray-200">
                    <CardContent className="p-6">
                      {/* Hospital Icon and Name */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-primary truncate">{hospital.name}</h3>
                          <Badge className="bg-[#14CAF4] text-white mt-1 text-xs">
                            Active
                          </Badge>
                        </div>
                      </div>

                      {/* Hospital Details */}
                      <div className="space-y-2 text-sm text-muted-foreground mb-4">
                        {/* Location */}
                        {(hospital.address || hospital.city) && (
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span className="truncate">
                              {hospital.address && hospital.city 
                                ? `${hospital.city}, ${hospital.state || ''}`.trim().replace(/,$/, '')
                                : hospital.address || hospital.city}
                            </span>
                          </div>
                        )}

                        {/* Phone */}
                        {hospital.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{hospital.phone}</span>
                          </div>
                        )}

                        {/* Email */}
                        {hospital.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{hospital.email}</span>
                          </div>
                        )}
                      </div>

                      {/* Statistics Footer */}
                      <div className="grid gap-2 pt-3 border-t border-gray-200">
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground mb-1">
                           Total Patients <span className="text-primary font-bold">{hospital.total_patients || hospital.totalPatients || 0}</span>
                          </p>                          
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Appointments Section */}
        {activeSection === 'appointments' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-primary text-xl sm:text-2xl">Appointments</h2>
                <p className="text-sm text-muted-foreground">Manage patient appointments</p>
              </div>
              <Button
                onClick={() => setShowScheduleAppointment(true)}
                className="bg-[#F07D13] hover:bg-[#F07D13]/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule Appointment
              </Button>
            </div>

            <Card>
              <CardContent className="p-6">
                {scheduledAppointments.length === 0 ? (
                  <EmptyState 
                    icon={Calendar}
                    title="No appointments scheduled"
                    description="Schedule your first appointment to get started"
                  />
                ) : (
                  <div className="space-y-4">
                    {scheduledAppointments.map((apt: any) => (
                      <Card key={apt.id} className="border-accent/20 hover:shadow-lg transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-4 h-4 text-accent" />
                                <h3 className="text-primary">{apt.patientName || apt.patient_name}</h3>
                              </div>
                              <div className="space-y-1 text-sm text-muted-foreground ml-6">
                                {apt.reason && (
                                  <p className="flex items-center gap-2 ">
                                    <MessageCircle className="w-3 h-3" />
                                    <b>{apt.reason}</b> 
                                  </p>
                                )}
                                <p className="flex items-center gap-2">
                                  <Stethoscope className="w-3 h-3" />
                                  Participants : {apt.doctorName || apt.doctor_name} (Dr.),  &nbsp;
                                  {apt.assistant_name} (Physician Asst.), &nbsp;
                                  {apt.therapist_name} (Therapist), &nbsp;
                                  {apt.nurse_name} (Home Nurse)
                                </p>
                                <p className="flex items-center gap-2">
                                  <Clock className="w-3 h-3" />
                                  {apt.date || apt.appointment_date} at {apt.time || apt.appointment_time}
                                </p>                                
                              </div>
                            </div>
                            <Badge className={`${
                              apt.status === 'Confirmed' || apt.status === 'CONFIRMED'
                                ? 'bg-[#14CAF4] text-white'
                                : apt.status === 'Pending' || apt.status === 'PENDING'
                                ? 'bg-[#F07D13] text-white'
                                : apt.status === 'Completed' || apt.status === 'COMPLETED'
                                ? 'bg-[#2762AC] text-white'
                                : 'bg-[#494949] text-white'
                            }`}>
                              {apt.status}
                            </Badge>
                            {canJoinAppointment(apt.scheduled_date) && (
                              <Button
                                size="sm"
                                onClick={() => handleJoinCall(apt)}
                                className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white text-xs ml-2"
                              >
                                <Video className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                Join Call
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
          </div>
        )}
        
        {/* Symptoms Section */}
        {activeSection === 'symptoms' && (
          <CoordinatorSymptomsTracker
            symptoms={allSymptoms}
            hospitals={hospitals}
            onViewHistory={(patient) => {
              setViewingPatient(patient);
              // Navigate to patient details or show modal
              toast.info('Viewing patient history...');
            }}
            onScheduleAppointment={(patient) => {
              setAppointmentData({
                patientId: patient.patient_id?.toString() || '',
                patientName: patient.patient_name,
                doctorId: '',
                doctorName: '',
                date: '',
                time: '',
                reason: 'Symptom follow-up',
                notes: `Patient reported: ${Array.isArray(patient.symptoms) ? patient.symptoms.join(', ') : patient.symptoms}`,
              });
              setShowScheduleAppointment(true);
            }}
            onEscalate={(symptomId) => {
              setEscalatedSymptoms(new Set([...escalatedSymptoms, symptomId.toString()]));
              toast.success('Symptom escalated successfully');
              // TODO: Call API to update symptom status
            }}
            onResolve={(symptomId) => {
              toast.success('Symptom marked as resolved');
              // TODO: Call API to update symptom status
            }}
          />
        )}
        
        {/* New Patients Onboarding Section */}
        {activeSection === 'onboarding' && !selectedOnboardingPatient && (
          <div className="space-y-6">
            {/* Header with Pending Badge */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-primary text-xl sm:text-2xl">New Patients Onboarding</h2>
                <p className="text-sm text-muted-foreground">Upload discharge summary for new patients</p>
              </div>
              {(dashboardData?.new_patients_pending_onboarding || []).filter(p => !completedOnboarding.includes(p.patient_id || p.id)).length > 0 && (
                <Badge className="bg-[#F07D13] text-white px-3 py-1">
                  {(dashboardData?.new_patients_pending_onboarding || []).filter(p => !completedOnboarding.includes(p.patient_id || p.id)).length} Pending
                </Badge>
              )}
            </div>

            {/* Patient Cards Grid */}
            {(dashboardData?.new_patients_pending_onboarding || []).filter(p => !completedOnboarding.includes(p.patient_id || p.id)).length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-12 h-12 text-[#14CAF4] mx-auto mb-4" />
                  <p className="text-muted-foreground">No pending onboarding</p>
                  <p className="text-sm text-muted-foreground mt-1">All patients have completed onboarding</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(dashboardData?.new_patients_pending_onboarding || [])
                  .filter(p => !completedOnboarding.includes(p.patient_id || p.id))
                  .map((patient: any) => {
                    // Generate initials from patient name
                    const getInitials = (name: string) => {
                      return name
                        .split(' ')
                        .map(n => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2);
                    };

                    return (
                      <Card key={patient.id} className="hover:shadow-md transition-shadow border border-gray-200">
                        <CardContent className="p-6">
                          <div className="space-y-4">
                            {/* Avatar and Patient Info */}
                            <div className="flex items-start gap-3">
                              {/* Circular Avatar */}
                              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white flex-shrink-0">
                                <span>{getInitials(patient.name)}</span>
                              </div>
                              
                              {/* Patient Details */}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-primary truncate">{patient.name}</h3>
                                <p className="text-sm text-muted-foreground">
                                {patient.age} years
                                </p>
                              </div>
                            </div>

                            {/* Hospital Info */}
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Building2 className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{patient.hospital_name || 'N/A'}</span>
                            </div>

                            {/* Discharge Summary Status */}
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <FileText className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">Discharge summary pending</span>
                            </div>
 
                            {/* Upload Button */}
                            <Button
                              className="w-full bg-primary hover:bg-primary/90 gap-2"
                              onClick={() => {
                                setSelectedPatientForUpload(patient);
                                setIsUploadDischargeSummaryModalOpen(true);
                              }}
                            >
                              <Upload className="w-4 h-4" />
                              Upload Discharge Summary
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            )}
          </div>
        )}
        
        {/* New Patients Onboarding Section - When patient selected */}
        {activeSection === 'onboarding' && selectedOnboardingPatient && (
          <DischargeSummaryOnboarding 
            patient={selectedOnboardingPatient}
            onComplete={() => {
              setCompletedOnboarding([...completedOnboarding, selectedOnboardingPatient.patient_id || selectedOnboardingPatient.id]);
              setSelectedOnboardingPatient(null);
              toast.success('Discharge summary completed!');
            }}
            onBack={() => setSelectedOnboardingPatient(null)}
          />
        )}
      </div>

      {/* Schedule Appointment Modal */}
      <ScheduleAppointmentModal
        isOpen={showScheduleAppointment}
        onClose={() => setShowScheduleAppointment(false)}
        onSuccess={() => {
          refetch(); // Reload dashboard data after scheduling
        }}
      />

      {/* Register Patient Modal */}
      <RegisterPatientModal
        open={isRegisterPatientModalOpen}
        onOpenChange={setIsRegisterPatientModalOpen}
        onSuccess={() => {
          refetch(); // Reload dashboard data after patient registration
        }}
        userRole="physician_assistant"
      />

      {/* Upload Discharge Summary Modal */}
      <UploadDischargeSummaryModal
        isOpen={isUploadDischargeSummaryModalOpen}
        onClose={() => {
          setIsUploadDischargeSummaryModalOpen(false);
          setSelectedPatientForUpload(null);
        }}
        patient={selectedPatientForUpload}
        onSuccess={() => {
          refetch(); // Reload dashboard data after upload
          setIsUploadDischargeSummaryModalOpen(false);
          setSelectedPatientForUpload(null);
        }}
      />
    </div>
  );
}